import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';

const PlatformHeader: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/authentication');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Get correct dashboard route based on user role
  const getDashboardRoute = () => {
    if (!userProfile) return '/';
    switch (userProfile.role) {
      case 'brand':
        return '/brand-dashboard';
      case 'creator':
        return '/creator-dashboard';
      case 'agency':
        return '/agency-dashboard';
      case 'admin':
        return '/brand-dashboard';
      default:
        return '/';
    }
  };

  // Get role-specific colors
  const getRoleColor = () => {
    if (!userProfile) return 'bg-primary/20';
    switch (userProfile.role) {
      case 'brand':
        return 'bg-blue-500/20';
      case 'creator':
        return 'bg-green-500/20';
      case 'agency':
        return 'bg-purple-500/20';
      case 'admin':
        return 'bg-primary/20';
      default:
        return 'bg-primary/20';
    }
  };

  const getRoleTextColor = () => {
    if (!userProfile) return 'text-primary';
    switch (userProfile.role) {
      case 'brand':
        return 'text-blue-400';
      case 'creator':
        return 'text-green-400';
      case 'agency':
        return 'text-purple-400';
      case 'admin':
        return 'text-primary';
      default:
        return 'text-primary';
    }
  };

  const getRoleBadge = () => {
    if (!userProfile) return null;
    const badges = {
      brand: { text: 'Brand', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      creator: { text: 'Creator', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      agency: { text: 'Agency', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      admin: { text: 'Admin', color: 'bg-primary/20 text-primary border-primary/30' },
    };
    return badges[userProfile.role as keyof typeof badges] || null;
  };

  if (!user || !userProfile) return null;

  const roleBadge = getRoleBadge();

  return (
    <header className="shadow-sm border-b border-dark-700 sticky top-0 z-50 bg-dark-900 backdrop-blur-sm">
      {/* Role-specific colored banner */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${
        userProfile.role === 'brand' ? 'bg-blue-500' :
        userProfile.role === 'creator' ? 'bg-green-500' :
        userProfile.role === 'agency' ? 'bg-purple-500' :
        'bg-primary'
      }`}></div>

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate(getDashboardRoute())}>
            <img
              src="/assets/images/Noonx_logo_small.png"
                loading="eager"
                fetchpriority="high"
              alt="NoonX Platform Logo - Modern influencer marketing platform connecting brands with creators"
              className="h-10 w-auto object-contain group-hover:opacity-80 transition-opacity"
            />
            {roleBadge && (
              <span className={`hidden md:inline-block px-2 py-1 rounded-full text-[10px] font-bold border ${roleBadge.color} uppercase tracking-wide`}>
                {roleBadge.text}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 px-4 py-2 bg-dark-800 rounded-lg border border-dark-700 hover:border-dark-600 transition-colors">
              <div className={`w-8 h-8 rounded-full ${getRoleColor()} flex items-center justify-center`}>
                <User className={`w-4 h-4 ${getRoleTextColor()}`} />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-white">{userProfile.full_name}</p>
                <p className={`capitalize text-xs font-medium ${getRoleTextColor()}`}>{userProfile.role}</p>
              </div>
            </div>
            
            <Button
              onClick={handleSignOut}
              variant="outline"
              className="flex items-center gap-2 border-dark-600 text-dark-300 hover:text-white hover:border-primary hover:bg-dark-700 transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default PlatformHeader;